import React, { useState, useEffect, useCallback, useRef } from 'react'
import InteractiveDialog from '../../pures/InteractiveDialog/InteractiveDialog'
import { ButtonHLPure, CheckBoxHLPure, DropDownBtnHLPure, GridSelectionHLPure, InstructionsPure, SearchBarHLPure, SimpleLoading } from '../../pures'
import { InteractiveItem } from '../../layouts'

const HeaderColumns = (props) => {
    return (
        <span className={(props.title === props.t('digitalVaultUploadView.grid.title.1') || props.title === props.t('digitalVaultUploadView.modal.entities.grid.title.1')) ? 'grid-header-one' : 'grid-headers'}>{props.title}</span>
    )
}

const AdministrationTypeDocumentsReassignModal = ({
    typeDocumentSelected,
    allTypeDocuments,
    onClose,
    onReassign,
    getAssignedItems,
    t
}) => {
    const [selectedItems, setSelectedItems] = useState(new Set())
    const [newTypeId, setNewTypeId] = useState('')
    const [inputValue, setInputValue] = useState('')
    const [searchValue, setSearchValue] = useState('')
    const debounceRef = useRef(null)
    const [page, setPage] = useState(1)
    const pageSize = 4
    const [assignedItems, setAssignedItems] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [isReassigning, setIsReassigning] = useState(false)

    const availableTypes = allTypeDocuments.filter(
        (td) => td.id !== typeDocumentSelected?.id
    ).map((td) => ({ id: td.document.id, name: td.document.name }))

    availableTypes.unshift({ id: '', name: t('configuration.AdministrationTypeDocuments.reassign.newTypePlaceholder') })

    const fetchItems = useCallback(async () => {
        if (!typeDocumentSelected?.id) return
        setIsLoading(true)
        try {
            const result = await getAssignedItems(typeDocumentSelected.id)
            if (result && result.data) {
                setAssignedItems(Array.isArray(result.data.documents) ? result.data.documents : [])
            } else {
                setAssignedItems([])
            }
        } catch {
            setAssignedItems([])
        } finally {
            setIsLoading(false)
        }
    }, [typeDocumentSelected, getAssignedItems, pageSize])

    useEffect(() => {
        fetchItems()
    }, [])

    /**
     * @function handleDebouncedSearch
     * @param {React.ChangeEvent<HTMLInputElement>} e
     * @returns {void}
     */
    const handleDebouncedSearch = (e) => {
        const value = e.target.value
        setInputValue(value)
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => {
            setSearchValue(value)
        }, 700)
    }

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [])

    const handleToggleItem = (id) => {
        setSelectedItems((prev) => {
            const next = new Set(prev)
            if (next.has(id)) {
                next.delete(id)
            } else {
                next.add(id)
            }
            return next
        })
    }

    const handleSelectAllPage = () => {
        const currentPageIds = assignedItems.map((item) => item.id)
        const allSelected = currentPageIds.every((id) => selectedItems.has(id))
        setSelectedItems((prev) => {
            const next = new Set(prev)
            if (allSelected) {
                currentPageIds.forEach((id) => next.delete(id))
            } else {
                currentPageIds.forEach((id) => next.add(id))
            }
            return next
        })
    }

    const handleReassign = async () => {
        if (selectedItems.size === 0 || !newTypeId) return
        setIsReassigning(true)
        const previousCount = assignedItems.length
        const reassignedCount = selectedItems.size
        try {
            const success = await onReassign({
                currentTypeDocumentId: typeDocumentSelected.id,
                targetTypeDocumentId: newTypeId,
                documents: !allPageSelected ? Array.from(selectedItems).map((id) => ({ id })) : [],
                reassignAll: allPageSelected,
            })
            if (success) {
                setSelectedItems(new Set())
                setNewTypeId('')
                if (previousCount <= reassignedCount) {
                    onClose()
                } else {
                    setPage(1)
                    await fetchItems(searchValue, 1)
                }
            }
        } finally {
            setIsReassigning(false)
        }
    }

    const filteredItems = assignedItems.filter((item) =>
        !searchValue || item.name?.toLowerCase().includes(searchValue.toLowerCase())
    )
    const currentPageIds = filteredItems.map((item) => item.id)
    const allPageSelected = currentPageIds.length > 0 && currentPageIds.every((id) => selectedItems.has(id))
    const canReassign = selectedItems.size > 0 && newTypeId && !isReassigning

    const AllHeadersColumns = (props) => <HeaderColumns {...props} t={t} />

    const CellDocumentWithCheckbox = (props) => {
        const { dataItem, onCheckChange } = props

        return (
            <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0' }}>
                    <CheckBoxHLPure
                        valueCheckBox={dataItem}
                        checkedState={dataItem.checked || false}
                        onChangeHandler={(event) => onCheckChange(event, dataItem)}
                    />
                    <div>
                        <p className='grid-cell-titles-margin-left-thirdParty-title grid-cell-title'>{dataItem.name}</p>
                        {/* <p className='grid-cell-titles-margin-left-thirdParty grid-cell-subtitle'>{dataItem.documentType}</p> */}
                    </div>
                </div>
            </td>
        )
    }

    return (
        <InteractiveDialog
            title={t('configuration.AdministrationTypeDocuments.reassign.title')}
            dialogSize='M'
            onClose={onClose}
            CustomButtons={
                <div className='reassign-modal__actions'>
                    <ButtonHLPure
                        nameBtn={t('button.return') || 'Regresar'}
                        onClickBtn={onClose}
                    />
                    <ButtonHLPure
                        nameBtn={isReassigning ? '...' : t('configuration.AdministrationTypeDocuments.reassign.button')}
                        primary
                        onClickBtn={handleReassign}
                        disabled={!canReassign}
                    />
                </div>
            }
        >
            {isLoading ? (
                <SimpleLoading />
            ) : (
                <div className='reassign-modal__content'>
                    {/* Current type (read only) */}
                    <div className='reassign-modal__current-type'>
                        <strong>{t('configuration.AdministrationTypeDocuments.reassign.currentType')}:</strong>
                        <span>{typeDocumentSelected?.document?.name || ''}</span>
                    </div>

                    {/* Instruction */}
                    <InstructionsPure instructions={[
                        t('configuration.AdministrationTypeDocuments.reassign.instruction.pt1'),
                        t('configuration.AdministrationTypeDocuments.reassign.instruction.pt2'),
                        t('configuration.AdministrationTypeDocuments.reassign.instruction.pt3')
                    ]} />

                    {/* Search bar */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: 'center' }}>
                        <CheckBoxHLPure
                            labelCheckBox={t('configuration.AdministrationTypeDocuments.reassign.selectAll')}
                            checkedState={allPageSelected}
                            onChangeHandler={handleSelectAllPage}
                        />
                        <SearchBarHLPure
                            searchValue={inputValue}
                            onSearchChange={handleDebouncedSearch}
                            placeholder={t('configuration.AdministrationTypeDocuments.reassign.search.placeholder')}
                        />
                    </div>
                    <GridSelectionHLPure
                        skip={0}
                        take={pageSize}
                        dataGrid={filteredItems.map((item) => ({ ...item, checked: selectedItems.has(item.id) }))}
                        noRecordsMessage={t('configuration.AdministrationTypeDocuments.reassign.noItems')}

                        cellColumnOne={(props) => <CellDocumentWithCheckbox {...props} onCheckChange={(_e, item) => handleToggleItem(item.id)} />}
                        headerColumnOne={AllHeadersColumns}
                        widthColumnOne={'auto'}
                        minResizableWidth1={'auto'}

                        styleGrid={{ height: 'auto', maxHeight: '250px' }}
                        onSelectedItem={() => { }}
                        setNameComponentUser={() => { }}
                        setShowDetails={() => { }}
                    />

                    <InteractiveItem style={{ marginTop: 80 }}>
                        <DropDownBtnHLPure
                            optionsDropDown={availableTypes}
                            optionsValue={newTypeId}
                            nameOption='name'
                            valueItem='id'
                            labelDropDown={t('configuration.AdministrationTypeDocuments.reassign.newType')}
                            onDropDownChange={(e) => setNewTypeId(e.target.value)}
                            disable={selectedItems.size === 0}
                            flex
                        />
                    </InteractiveItem>

                    {selectedItems.size > 0 && (
                        <p className='reassign-modal__counter'>
                            {t('configuration.AdministrationTypeDocuments.reassign.counter', { count: selectedItems.size })}
                        </p>
                    )}

                </div>
            )}
        </InteractiveDialog>
    )
}

export default AdministrationTypeDocumentsReassignModal
